import PrimaryButton from '@/components/Buttons/PrimaryButton';
import SecondaryButton from '@/components/Buttons/SecondaryButton';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { Evaluation } from '@/types';
import { cleanWordPressHtml } from '@/utils/cleanHtml';
import { router } from '@inertiajs/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface WrongAnswerData {
    question_id: number;
    question: string;
    selected_answers: {
        id: number;
        answer: string;
    }[];
}

interface ResultsData {
    score: number;
    total_score: number;
    percentage: number;
    passed: boolean;
    correct_answers: number;
    incorrect_answers: number;
    unanswered_questions: number;
    total_questions: number;
    wrong_answers: WrongAnswerData[];
    time_taken_minutes: number;
    submitted_at: string;
}

interface ResultsProps {
    evaluation: Evaluation;
    results: ResultsData;
    can_retake: boolean;
    next_retake_time: string | null;
    course: {
        slug: string;
    };
    has_certificate_template: boolean;
}

const ResultsContent: React.FC<ResultsProps> = ({ evaluation, results, can_retake, next_retake_time, course, has_certificate_template }) => {
    const { t } = useTranslation();
    const pageTitle = `Resultados - ${evaluation.title}`;

    const formatTime = (minutes: number) => {
        if (minutes < 60) {
            return t('evaluation.results.minutesFormat', { count: Math.round(minutes) });
        }
        
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = Math.round(minutes % 60);
        
        if (remainingMinutes === 0) {
            return t('evaluation.results.hoursFormat', { count: hours });
        }
        
        return t('evaluation.results.hoursMinutesFormat', { 
            hours, 
            minutes: remainingMinutes 
        });
    };

    const getScoreColor = () => {
        if (results.percentage >= 80) return 'text-green-600';
        if (results.percentage >= 60) return 'text-yellow-600';
        return 'text-red-600';
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleReturnToCourse = () => {
        router.get(`/courses/${course.slug}/`);
    };

    const handleRetakeEvaluation = () => {
        router.get(`/evaluations/${evaluation.id}/`);
    };

    const handleViewCertificate = () => {
        router.get(`/evaluations/${evaluation.id}/certificate/`);
    };

    const formatRetakeTime = () => {
        if (!next_retake_time) return '';
        
        const retakeDate = new Date(next_retake_time);
        const now = new Date();
        const timeLeft = retakeDate.getTime() - now.getTime();
        
        if (timeLeft <= 0) return '';
        
        const hoursLeft = Math.floor(timeLeft / (1000 * 60 * 60));
        const minutesLeft = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
        
        if (hoursLeft > 0) {
            return `${hoursLeft}h ${minutesLeft}m`;
        }
        return `${minutesLeft}m`;
    };

    return (
        <AppLayout>
            <PageTitle title={pageTitle} />
            <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8">
                <div className="max-w-3xl w-full bg-white rounded-lg shadow-lg overflow-hidden">
                    {/* Header */}
                    <div className="px-8 py-6 border-b border-gray-200">
                        <div className="text-center">
                            <h1 className="text-2xl font-bold text-gray-800 mb-2">
                                {t('evaluation.results.title')}
                            </h1>
                            <p className="text-gray-600">
                                {evaluation.title}
                            </p>
                        </div>
                    </div>

                    {/* Result Status */}
                    <div className={`px-8 py-6 border-b border-gray-200 ${
                        results.passed ? 'bg-green-50' : 'bg-red-50'
                    }`}>
                        <div className="text-center">
                            {results.passed ? (
                                <div>
                                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500 flex items-center justify-center">
                                        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <h2 className="text-2xl font-bold text-green-800 mb-2">
                                        {t('evaluation.results.passed')}
                                    </h2>
                                    <p className="text-green-700">
                                        {t('evaluation.results.congratulations')}
                                    </p>
                                </div>
                            ) : (
                                <div>
                                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500 flex items-center justify-center">
                                        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <h2 className="text-2xl font-bold text-red-800 mb-2">
                                        {t('evaluation.results.failed')}
                                    </h2>
                                    <p className="text-red-700">
                                        {t('evaluation.results.tryAgain')}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Score Display */}
                    <div className="px-8 py-6 border-b border-gray-200">
                        <div className="text-center">
                            <div className={`text-6xl font-bold mb-2 ${getScoreColor()}`}>
                                {Math.round(results.percentage)}%
                            </div>
                            <p className="text-gray-600 mb-4">
                                {t('evaluation.results.finalScore')}
                            </p>
                            <p className="text-sm text-gray-500">
                                {t('evaluation.results.scoreDetails', {
                                    score: results.score,
                                    total: results.total_score,
                                    passing: evaluation.passing_score
                                })}
                            </p>
                        </div>
                    </div>

                    {/* Detailed Statistics */}
                    <div className="px-8 py-6 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-800 mb-6">
                            {t('evaluation.results.detailedResults')}
                        </h3>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-green-600 mb-1">
                                    {results.correct_answers}
                                </div>
                                <p className="text-sm text-gray-600">
                                    {t('evaluation.results.correct')}
                                </p>
                            </div>
                            
                            <div className="text-center">
                                <div className="text-2xl font-bold text-red-600 mb-1">
                                    {results.incorrect_answers}
                                </div>
                                <p className="text-sm text-gray-600">
                                    {t('evaluation.results.incorrect')}
                                </p>
                            </div>
                            
                            <div className="text-center">
                                <div className="text-2xl font-bold text-gray-600 mb-1">
                                    {results.unanswered_questions}
                                </div>
                                <p className="text-sm text-gray-600">
                                    {t('evaluation.results.unanswered')}
                                </p>
                            </div>
                            
                            <div className="text-center">
                                <div className="text-2xl font-bold text-gray-800 mb-1">
                                    {results.total_questions}
                                </div>
                                <p className="text-sm text-gray-600">
                                    {t('evaluation.results.total')}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Wrong Answers Review */}
                    {results.wrong_answers.length > 0 && (
                        <div className="px-8 py-6 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-800 mb-6">
                                {t('evaluation.results.wrongAnswersTitle')}
                            </h3>
                            <div className="space-y-4">
                                {results.wrong_answers.map((wrongAnswer, index) => (
                                    <div key={wrongAnswer.question_id} className="rounded-lg border border-red-100 bg-red-50 p-4">
                                        <p className="mb-2 text-sm font-semibold text-red-800">
                                            {t('evaluation.results.questionLabel', { index: index + 1 })}
                                        </p>
                                        <p
                                            className="mb-3 text-sm text-gray-800"
                                            dangerouslySetInnerHTML={{ __html: cleanWordPressHtml(wrongAnswer.question) }}
                                        />
                                        <p className="text-sm text-gray-700">
                                            <span className="font-semibold text-red-700">
                                                {t('evaluation.results.yourAnswerLabel')}
                                            </span>{' '}
                                            <span>
                                                {wrongAnswer.selected_answers
                                                    .map((answer) => cleanWordPressHtml(answer.answer))
                                                    .join(', ') || t('evaluation.review.notAnswered')}
                                            </span>
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Additional Info */}
                    <div className="px-8 py-6 border-b border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h4 className="font-medium text-gray-800 mb-2">
                                    {t('evaluation.results.timeTaken')}
                                </h4>
                                <p className="text-gray-600">
                                    {formatTime(results.time_taken_minutes)}
                                </p>
                            </div>
                            
                            <div>
                                <h4 className="font-medium text-gray-800 mb-2">
                                    {t('evaluation.results.submittedAt')}
                                </h4>
                                <p className="text-gray-600">
                                    {formatDate(results.submitted_at)}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Retake Info */}
                    {!results.passed && (
                        <div className="px-8 py-6 border-b border-gray-200">
                            <div className="text-center">
                                {can_retake ? (
                                    <div className="bg-theme-light rounded-lg p-6">
                                        <h3 className="font-semibold text-theme-text mb-2">
                                            {t('evaluation.results.retakeAvailable')}
                                        </h3>
                                        <p className="text-theme-text text-sm mb-4">
                                            {t('evaluation.results.retakeMessage')}
                                        </p>
                                    </div>
                                ) : next_retake_time && (
                                    <div className="bg-yellow-50 rounded-lg p-6">
                                        <h3 className="font-semibold text-yellow-800 mb-2">
                                            {t('evaluation.results.retakeCooldown')}
                                        </h3>
                                        <p className="text-yellow-700 text-sm">
                                            {t('evaluation.results.retakeAvailableIn', {
                                                time: formatRetakeTime()
                                            })}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Certificate Section */}
                    {results.passed && has_certificate_template && (
                        <div className="px-8 py-6 border-b border-gray-200">
                            <div className="text-center">
                                <div className="bg-green-50 rounded-lg p-6">
                                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500 flex items-center justify-center">
                                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <h3 className="font-semibold text-green-800 mb-2">
                                        {t('evaluation.certificate.title')}
                                    </h3>
                                    <p className="text-green-700 text-sm mb-4">
                                        {t('evaluation.certificate.description')}
                                    </p>
                                    <PrimaryButton onClick={handleViewCertificate} size="md">
                                        {t('evaluation.certificate.viewButton')}
                                    </PrimaryButton>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="px-8 py-6">
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            {!results.passed && can_retake && (
                                <PrimaryButton onClick={handleRetakeEvaluation} size="md">
                                    {t('evaluation.results.retakeEvaluation')}
                                </PrimaryButton>
                            )}
                            
                            <SecondaryButton
                                onClick={handleReturnToCourse}
                                size="md"
                            >
                                {t('evaluation.results.returnToCourse')}
                            </SecondaryButton>
                        </div>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
};

const Results: React.FC<ResultsProps> = (props) => {
    return (
        <ThemeWrapper>
            <ResultsContent {...props} />
        </ThemeWrapper>
    );
};

export default Results;
